import { useEffect, useState } from "react";

import instance from "@store/middleware/api";

import { useAuth } from "@/hooks/auth";

/**
 * Shared "is this account whitelisting-suspended" signal for the purchase freeze
 * (ToU §17.4): while suspended (a released video's Credit Text was removed), the
 * cart / buy-licence / subscribe / upgrade CTAs are blocked — they stay in their
 * normal state and route to the Account Issues modal via
 * `useWhitelistingPausedRedirect` rather than dimming into a dead end. One
 * cached fetch is shared across every consumer (the CTAs + the global banner
 * could all read this) instead of each component hitting the endpoint.
 *
 * Call `refreshWhitelistingSuspended()` after a resume/reactivate so the greyed
 * state clears live without a reload.
 */
let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

function notify(v: boolean): void {
  cached = v;
  listeners.forEach((l) => l(v));
}

export function refreshWhitelistingSuspended(): Promise<boolean> {
  inflight = instance
    .get("/claims/my/whitelisting-suspension-info")
    .then(({ data }) => {
      // Grey out purchases/downloads ONLY while a required Credit Text is
      // genuinely missing. Once every flagged video is fixed the account is
      // compliant — even if a lapsed Subscription is still awaiting a
      // Reactivation Election — so the freeze lifts (ToU §17.4(a)).
      const s = !!data?.suspended && !data?.awaitingReactivation;
      notify(s);
      return s;
    })
    .catch(() => {
      notify(false);
      return false;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useWhitelistingSuspended(): boolean {
  const { myData } = useAuth();
  const userId = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [suspended, setSuspended] = useState<boolean>(cached ?? false);

  useEffect(() => {
    if (!userId) {
      setSuspended(false);
      return;
    }
    const listener = (v: boolean) => setSuspended(v);
    listeners.add(listener);
    if (cached !== null) setSuspended(cached);
    if (cached === null && !inflight) refreshWhitelistingSuspended();
    return () => {
      listeners.delete(listener);
    };
  }, [userId]);

  return suspended;
}
